import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Clock, MapPin, TrendingUp, Car, Search, Filter, 
  ArrowUpDown, ChevronDown, Calendar, RefreshCw, Eye,
  Award, Fuel, DollarSign, Activity
} from 'lucide-react';
import api from '../../services/api';
import { format, startOfDay, endOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { sq } from 'date-fns/locale';
import { formatCurrency, formatEuro, lekToEuro } from '../../utils/formatters';

const DriverPerformancePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [totals, setTotals] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filters
  const [period, setPeriod] = useState('today');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sortBy, setSortBy] = useState('rides');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchPerformance = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        period,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      if (period === 'custom' && customDateFrom && customDateTo) {
        params.date_from = customDateFrom;
        params.date_to = customDateTo;
      }
      
      const response = await api.get('/performance/drivers', { params });
      setDrivers(response.data.drivers || []);
      setTotals(response.data.totals || {});
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  }, [period, sortBy, sortOrder, customDateFrom, customDateTo]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = !searchTerm || 
      driver.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || driver.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatHours = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    }
    return `${hours.toFixed(1)} orë`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      online: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', label: 'Online' },
      offline: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400', label: 'Offline' },
      busy: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500', label: 'Në udhëtim' }
    };
    const badge = badges[status] || badges.offline;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <span className={`w-2 h-2 rounded-full ${badge.dot} animate-pulse`} />
        {badge.label}
      </span>
    );
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today': return 'Sot';
      case 'week': return 'Kjo Javë';
      case 'month': return 'Ky Muaj';
      case 'year': return 'Ky Vit';
      case 'custom': return 'Periudhë';
      default: return 'Sot';
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortableHeader = ({ field, label }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortBy === field ? 'text-red-600' : 'text-gray-400'}`} />
      </div>
    </th>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performanca e Shoferëve</h1>
          <p className="text-sm text-gray-500">Monitoroni aktivitetin dhe performancën e çdo shoferi</p>
        </div>
        <button 
          onClick={fetchPerformance}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Shoferë Total</p>
              <p className="text-3xl font-bold mt-1">{totals.total_drivers || 0}</p>
              <p className="text-blue-200 text-xs mt-1">{totals.online_drivers || 0} online</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Rrugë Total ({getPeriodLabel()})</p>
              <p className="text-3xl font-bold mt-1">{totals.total_rides || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Car className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Orë Online ({getPeriodLabel()})</p>
              <p className="text-3xl font-bold mt-1">{totals.total_hours || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">KM Total ({getPeriodLabel()})</p>
              <p className="text-3xl font-bold mt-1">{totals.total_km || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <MapPin className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Xhiro Total ({getPeriodLabel()})</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(totals.total_earnings || 0)}</p>
              <p className="text-yellow-200 text-sm mt-1">{formatEuro(lekToEuro(totals.total_earnings || 0))}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="flex-1 min-w-[250px] relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Kërko sipas emrit ose targës..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Date Filter */}
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Calendar className="w-4 h-4 text-gray-500" />
            <span>{getPeriodLabel()}</span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          
          {showDatePicker && (
            <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-72 p-4">
              <div className="space-y-2">
                {[
                  { key: 'today', label: 'Sot' },
                  { key: 'week', label: 'Kjo Javë' },
                  { key: 'month', label: 'Ky Muaj' },
                  { key: 'year', label: 'Ky Vit' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setPeriod(opt.key); setShowDatePicker(false); }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition ${
                      period === opt.key ? 'bg-red-50 text-red-600 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Periudhë e Personalizuar</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <button
                    onClick={() => { setPeriod('custom'); setShowDatePicker(false); }}
                    disabled={!customDateFrom || !customDateTo}
                    className="w-full mt-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    Apliko
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        >
          <option value="all">Të gjithë Statusët</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="busy">Në Udhëtim</option>
        </select>

        {/* Sort */}
        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [field, order] = e.target.value.split('-');
            setSortBy(field);
            setSortOrder(order);
          }}
          className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        >
          <option value="rides-desc">Më shumë Rrugë</option>
          <option value="earnings-desc">Më shumë Xhiro</option>
          <option value="hours-desc">Më shumë Orë</option>
          <option value="km-desc">Më shumë KM</option>
          <option value="rides-asc">Më pak Rrugë</option>
          <option value="earnings-asc">Më pak Xhiro</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shoferi</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <SortableHeader field="hours" label="Orë Online" />
              <SortableHeader field="km" label="KM" />
              <SortableHeader field="rides" label="Rrugë" />
              <SortableHeader field="earnings" label="Xhiro" />
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Mesatare/Orë</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Veprime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-4 py-12 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Duke ngarkuar...</p>
                </td>
              </tr>
            ) : filteredDrivers.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Nuk u gjetën shoferë</p>
                </td>
              </tr>
            ) : (
              filteredDrivers.map((driver, index) => (
                <tr 
                  key={driver.driver_id} 
                  className="hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => navigate(`/admin/drivers/${driver.driver_id}/profile`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white text-sm font-bold">
                        {driver.driver_name?.charAt(0) || 'D'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{driver.driver_name}</p>
                        <p className="text-xs text-gray-500">{driver.vehicle_plate}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(driver.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-purple-500" />
                      <span className="font-medium">{formatHours(driver.total_online_hours)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-500" />
                      <span className="font-medium">{driver.total_km} km</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-green-500" />
                      <span className="font-medium">{driver.total_rides}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-bold text-yellow-600">{formatCurrency(driver.total_earnings_lek)}</span>
                      <div className="text-xs text-gray-500">{formatEuro(lekToEuro(driver.total_earnings_lek))}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-gray-600">
                      {driver.avg_rides_per_hour} rrugë
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/drivers/${driver.driver_id}/profile`);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="Shiko Profilin"
                    >
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          Online
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-yellow-500" />
          Në Udhëtim
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-gray-400" />
          Offline
        </div>
        <div className="text-gray-400">|</div>
        <div>Klikoni mbi shofer për profilin e plotë</div>
      </div>
    </div>
  );
};

export default DriverPerformancePage;
